import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BRAZIL_STATES, regionFromUF } from "@/lib/brazil";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import SupporterFields from "@/components/SupporterFields";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [supporter, setSupporter] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "",
    state: "", city: "", child_name: "", child_condition: "", child_birth_date: "",
    document_type: "CPF", document_number: "",
  });
  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("A senha deve ter no mínimo 6 caracteres");
      return;
    }
    if (!form.phone) {
      toast.error("Informe seu WhatsApp — usamos para o pré-atendimento.");
      return;
    }
    if (!form.state || !form.city) {
      toast.error("Informe cidade e estado.");
      return;
    }
    setLoading(true);
    const region = form.state ? regionFromUF(form.state) : null;
    const res = await register({ ...form, region, supporter });
    setLoading(false);
    if (res.ok) {
      toast.success("Cadastro realizado! Bem-vindo(a).");
      navigate("/timeline", { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="container-cuidar py-14 md:py-20">
      <div className="mx-auto max-w-2xl">
        <div className="mb-8 flex flex-col items-start gap-5">
          <img src="/logo-instituto-cuidar.png" alt="Instituto Cuidar" className="h-14 w-auto object-contain" />
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black">Criar conta gratuita</h1>
            <p className="mt-2 text-[hsl(var(--muted-foreground))]">
              Faça parte da comunidade Instituto Cuidar. Seus dados são sigilosos e usados apenas para o atendimento à sua família.
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-6">
          {/* Sobre você */}
          <Section title="Sobre você">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nome completo" required id="name">
                <Input id="name" required value={form.name} onChange={(e) => setField("name")(e.target.value)}
                  className="h-12 rounded-xl" data-testid="register-name-input" />
              </Field>
              <Field label="E-mail" required id="email">
                <Input id="email" type="email" required value={form.email} onChange={(e) => setField("email")(e.target.value)}
                  className="h-12 rounded-xl" data-testid="register-email-input" />
              </Field>
              <Field label="Senha (mín. 6)" required id="password">
                <Input id="password" type="password" required value={form.password} onChange={(e) => setField("password")(e.target.value)}
                  className="h-12 rounded-xl" data-testid="register-password-input" />
              </Field>
              <Field label="Contato de WhatsApp" required id="phone" hint="Usaremos para o pré-atendimento pelo WhatsApp.">
                <Input id="phone" required value={form.phone} onChange={(e) => setField("phone")(e.target.value)}
                  placeholder="(11) 99999-9999" className="h-12 rounded-xl" data-testid="register-phone-input" />
              </Field>
            </div>
          </Section>

          {/* Localização */}
          <Section title="Onde você está">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Estado" required id="state">
                <Select value={form.state} onValueChange={setField("state")}>
                  <SelectTrigger className="h-12 rounded-xl" data-testid="register-state-select">
                    <SelectValue placeholder="Selecione seu estado" />
                  </SelectTrigger>
                  <SelectContent>
                    {BRAZIL_STATES.map((s) => (
                      <SelectItem key={s.uf} value={s.uf} data-testid={`register-state-option-${s.uf}`}>
                        {s.name} ({s.uf}) · {s.region}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Cidade" required id="city">
                <Input id="city" required value={form.city} onChange={(e) => setField("city")(e.target.value)}
                  className="h-12 rounded-xl" data-testid="register-city-input" />
              </Field>
            </div>
          </Section>

          {/* Sobre a criança */}
          <Section
            title="Sobre a criança (opcional agora)"
            description="Você pode pular esta parte e completar depois. Porém, a Carteirinha do Instituto e a maioria dos benefícios só ficam disponíveis com o cadastro completo da criança (nome, data de nascimento, situação neuroatípica) e do documento."
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nome da criança" id="child_name">
                <Input id="child_name" value={form.child_name} onChange={(e) => setField("child_name")(e.target.value)}
                  className="h-12 rounded-xl" data-testid="register-child-name-input" />
              </Field>
              <Field label="Data de nascimento" id="child_birth_date">
                <Input id="child_birth_date" type="date"
                  max={new Date().toISOString().slice(0, 10)}
                  value={form.child_birth_date} onChange={(e) => setField("child_birth_date")(e.target.value)}
                  className="h-12 rounded-xl" data-testid="register-child-birth-input" />
              </Field>
            </div>
            <Field label="Situação neuroatípica" id="child_condition" hint="Descreva o diagnóstico ou suspeita — TEA, TDAH, dislexia, síndrome de Down, atraso na fala, etc.">
              <Textarea id="child_condition" rows={3}
                placeholder="Ex: TEA nível 1 diagnosticado há 6 meses, em terapia ABA…"
                value={form.child_condition} onChange={(e) => setField("child_condition")(e.target.value)}
                className="rounded-xl" data-testid="register-child-condition-input" />
            </Field>
          </Section>

          {/* Documento */}
          <Section title="Documento de identificação (opcional agora)" description="Necessário para emitir a Carteirinha do Instituto e liberar todos os benefícios.">
            <div className="grid sm:grid-cols-[180px_1fr] gap-4">
              <Field label="Tipo" id="document_type">
                <Select value={form.document_type} onValueChange={setField("document_type")}>
                  <SelectTrigger className="h-12 rounded-xl" data-testid="register-doctype-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CPF" data-testid="register-doctype-option-cpf">CPF</SelectItem>
                    <SelectItem value="RG" data-testid="register-doctype-option-rg">RG</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label={`Número do ${form.document_type}`} id="document_number">
                <Input
                  id="document_number"
                  placeholder={form.document_type === "CPF" ? "000.000.000-00" : "00.000.000-0"}
                  value={form.document_number}
                  onChange={(e) => setField("document_number")(e.target.value)}
                  className="h-12 rounded-xl"
                  data-testid="register-docnumber-input"
                />
              </Field>
            </div>
          </Section>

          {/* Apoiador (opcional) */}
          <Section
            title="Também quer apoiar o Instituto?"
            description="Marque a caixa abaixo para se cadastrar como voluntário(a) e/ou doador(a). Você pode ativar ou alterar isso a qualquer momento no seu perfil."
          >
            <SupporterFields value={supporter} onChange={setSupporter} compact />
          </Section>

          <div className="pt-2">
            <Button
              type="submit" disabled={loading} data-testid="register-submit-btn"
              className="btn-pill w-full h-12 bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"
            >
              {loading ? "Cadastrando…" : "Criar conta"}
            </Button>
            <div className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">
              Já tem conta?{" "}
              <Link to="/entrar" className="link-underline text-[hsl(var(--primary))] font-medium" data-testid="register-goto-login">Entrar</Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ title, description, children }) {
  return (
    <div className="surface p-6 sm:p-7">
      <div className="mb-4">
        <div className="font-heading text-lg font-extrabold text-[hsl(var(--foreground))]">{title}</div>
        {description && <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{description}</div>}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, id, required, hint, children }) {
  return (
    <div>
      <Label htmlFor={id} className="text-sm">
        {label} {required && <span className="text-[hsl(var(--destructive))]">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="mt-1.5 text-xs text-[hsl(var(--muted-foreground))]">{hint}</div>}
    </div>
  );
}
